/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `UserDisplayModal` for the Caintra Connect frontend.
 * @module modals/UserDisplayModal
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router";
import { getCompanyByRFC } from "../logic/company/index.js";
import { updateUser } from "../logic/user/index.js";
import styles from "../styles/Users.module.css";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import { SvgIcon, Backdrop } from "@mui/material";
import { ReactComponent as BadgeIcon } from "../assets/icons/Badge.svg";
import { ReactComponent as MailIcon } from "../assets/icons/Mail.svg";
import { ReactComponent as BusinessCenterIcon } from "../assets/icons/Business_Center.svg";
import { ReactComponent as EditIcon } from "../assets/icons/Edit.svg";
import UserEditModal from "./UserEditModal.jsx";
import AssetImage from "../components/AssetImage";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getApiErrorMessage } from "../utils/apiErrorMessages.js";
import { useTranslation } from "react-i18next";

/**
 * Modal that displays a user's details (name, member number, company, status, title,
 * email, phone, RFC) and provides actions to edit or toggle active state. Used on desktop
 * when the user clicks a row in the Users list to view details.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {function} props.onClose - Called when the modal is closed
 * @param {Object} props.user - The user to display
 * @param {function} props.onUpdateUser - Called when the user is updated (e.g. after edit or toggle active)
 * @param {Object} [props.currentUser] - The currently logged-in user; used to show/hide edit and save actions by role
 */
const UserDisplayModal = ({
  open,
  onClose,
  user,
  onUpdateUser,
  currentUser,
}) => {
  const navigate = useNavigate();
  const profilePic = useSelector(() => user.imagenDePerfil);
  const { t } = useTranslation();

  const [tempImage, setTempImage] = useState(null);
  const [imageKey, setImageKey] = useState(Date.now());
  const [activo, setIsActive] = useState(false);
  const [unsubscribedFromCustomEmails, setUnsubscribedFromCustomEmails] =
    useState(false);
  const [imagenDePerfil, setProfileImage] = useState("");
  const [imagenDePerfilFormato, setProfileImageFormat] = useState("");
  const [numeroSocio, setMemberNumber] = useState("");
  const [tipoDeUsuario, setUserType] = useState("");
  const [nombre, setName] = useState("");
  const [titulo, setTitle] = useState("");
  const [correoElectronico, setEmail] = useState("");
  const [numeroCelular, setNumeroCelular] = useState("");
  const [RFC, setRFC] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [loading, setLoading] = useState(false); // Loading state
  const [company, setCompany] = useState(null);
  const [isLoadingCompany, setIsLoadingCompany] = useState(false);
  const dialogRef = useRef(null);
  const toastId = useRef(null);

  /* --- Update (delegate to logic) --- */

  /**
   * Sends updated user data (activo, imagenDePerfil) to the API. On success calls onUpdateUser and onClose; on error shows toast.
   */
  const handleUpdateUser = async () => {
    if (!user._id) {
      logger.error("User ID is missing");
      return;
    }

    const updatedUserData = {
      ...user,
      activo,
      unsubscribedFromCustomEmails,
      unsubscribedFromCustomEmailsAt: unsubscribedFromCustomEmails
        ? user.unsubscribedFromCustomEmailsAt || new Date().toISOString()
        : null,
      unsubscribedFromCustomEmailsSource: unsubscribedFromCustomEmails
        ? user.unsubscribedFromCustomEmailsSource || "admin"
        : null,
      imagenDePerfil,
      imagenDePerfilFormato,
    };

    setLoading(true);
    try {
      await updateUser(user._id, updatedUserData, {
        navigate,
        onSuccess: (updatedUser) => {
          onUpdateUser(updatedUser);
          setImageKey(Date.now());
          onClose();
        },
        onError: (errorData) => {
          const errorType = errorData?.errorType;
          toastId.current = errorType;
          toast.error(getApiErrorMessage({ errorType }), {
            toastId: toastId.current,
          });
        },
      });
    } catch (error) {
      logger.error("Error:", { err: sanitizeError(error) });
    } finally {
      setLoading(false);
    }
  };

  /* --- Effects (dialog lifecycle and user/company data) --- */

  /**
   * Manages dialog open/close: shows or closes the native dialog and wires close handler to dismiss toast and reset state.
   * Listens to dialog "close" event for cleanup.
   */
  useEffect(() => {
    const handleClose = () => {
      toast.dismiss(toastId.current);
      setTempImage(null);
      setIsActive(user.activo);
      setUnsubscribedFromCustomEmails(
        user.unsubscribedFromCustomEmails === true,
      );
      onClose();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open) {
      dialog.showModal();
    } else {
      dialog.close();
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, [open, onClose]);

  /**
   * Syncs local state with the displayed user when the modal opens or the user changes.
   * Ensures Cancel/X discards local switch edits on next open.
   */
  useEffect(() => {
    if (!open || !user) {
      return;
    }

    setIsActive(user.activo);
    setUnsubscribedFromCustomEmails(user.unsubscribedFromCustomEmails === true);
    setProfileImage(user.imagenDePerfil);
    setProfileImageFormat(user.imagenDePerfilFormato);
    setUserType(user.nombreRol);
    setName(user.nombre);
    setTitle(user.titulo);
    setEmail(user.correoElectronico);
    setNumeroCelular(user.numeroCelular ?? "");
    setRFC(user.RFC);
    setImageKey(Date.now());
    setCompany(null);
    setMemberNumber("");
    setIsLoadingCompany(false);
  }, [open, user]);

  /**
   * Fetches company data by user RFC when user.RFC is available and updates company state.
   * Uses getCompanyByRFC; sets company to {} on error to avoid repeated requests.
   */
  useEffect(() => {
    const fetchCompanyData = async () => {
      if (user?.RFC && !isLoadingCompany && !company) {
        setIsLoadingCompany(true);
        try {
          const companyData = await getCompanyByRFC(user.RFC, navigate);
          if (companyData) {
            setCompany(companyData);
            setMemberNumber(companyData.numeroSocio ?? "");
          } else {
            // Set company to empty object to prevent further attempts
            setCompany({});
          }
        } catch (error) {
          logger.error("Error fetching company data:", {
            err: sanitizeError(error),
          });
          // Set company to empty object to prevent further attempts
          setCompany({});
        } finally {
          setIsLoadingCompany(false);
        }
      }
    };

    fetchCompanyData();
  }, [user?.RFC, navigate]);

  /* --- Action handlers --- */

  /**
   * Toggles the active/inactive state for the displayed user (local state; persisted on save).
   * Switch is disabled when current user cannot edit (e.g. role check).
   */
  const handleToggleActive = () => {
    setIsActive((prevActive) => !prevActive);
  };

  /**
   * Toggles custom-email unsubscribe (local state; persisted on save).
   * Switch ON = subscribed (receives mass emails); OFF = unsubscribed.
   */
  const handleToggleCustomEmailSubscription = () => {
    setUnsubscribedFromCustomEmails((prev) => !prev);
  };

  /**
   * Opens the UserEditModal for editing the current user.
   * Resets tempImage when opening.
   */
  const handleEditClick = () => {
    setEditModalOpen(true);
    setTempImage(null);
  };

  /**
   * Called when the user is updated from the edit modal. Passes the updated user to the parent and closes the edit modal.
   * @param {Object} updatedUser - The user object returned from the update API.
   * Closes the edit modal and passes updated user to parent.
   */
  const handleUpdateUserFromEditModal = (updatedUser) => {
    onUpdateUser(updatedUser);
    setEditModalOpen(false);
  };

  return (
    <>
      {open && !editModalOpen && <div className={styles.overlay}></div>}
      <dialog ref={dialogRef} className={styles.userDisplayModal}>
        <div className={styles.closeButtonContainer}>
          <button
            className={styles.closeButton}
            onClick={() => {
              toast.dismiss(toastId.current);
              onClose();
            }}
          >
            &times;
          </button>
        </div>
        <div className={styles.modalAvatarBoxContainer}>
          <div className={styles.modalAvatarBox}>
            <div className={styles.modalAvatar}>
              {imagenDePerfil ? (
                <AssetImage
                  src={tempImage || profilePic || imagenDePerfil}
                  alt={t("common.avatarAlt")}
                  className={styles.avatarImage}
                />
              ) : nombre ? (
                nombre.charAt(0)
              ) : (
                ""
              )}
            </div>
          </div>
        </div>

        <div className={styles.userDisplayModal_Content}>
          <div className={styles.userDisplayModal_UserRoleContainer}>
            <div className={styles.userRole}>
              {tipoDeUsuario && (
                <span className={styles.userRoleBadge}>{tipoDeUsuario}</span>
              )}
            </div>
          </div>

          <div className={styles.userNameContainer}>
            <h1 className={styles.userDisplayModal_UserName}>{nombre}</h1>
          </div>

          {numeroSocio && (
            <div className={styles.userDisplayModal_MemberNumberContainer}>
              <p className={styles.userDisplayModal_MemberNumber}>
                {t("components.userDisplayModal.memberNumber")}: {numeroSocio}
              </p>
            </div>
          )}

          <div className={styles.userDisplayModal_CompanyContainer}>
            <p className={styles.userDisplayModal_Company}>
              {company?.nombreComercial || t("common.defaultCompanyName")}
            </p>
          </div>

          <div className={styles.userDisplayModal_IsActiveContainer}>
            {activo ? (
              <span
                className={`${styles.userDisPlayModal_ActiveCircleBackground} material-symbols-outlined`}
              >
                how_to_reg
              </span>
            ) : (
              <span
                className={`${styles.userDisPlayModal_InactiveCircleBackground} material-symbols-outlined`}
              >
                person_off
              </span>
            )}
            <p className={styles.userDisplayModal_IsActive}>
              {activo
                ? t("components.userDisplayModal.active")
                : t("components.userDisplayModal.inactive")}
            </p>
            {currentUser?.tipoDeUsuario !== "3" &&
              currentUser?.tipoDeUsuario !== "4" &&
              currentUser?.tipoDeUsuario !== "12" && (
                <Switch
                  checked={activo}
                  onChange={handleToggleActive}
                  color="primary"
                />
              )}
          </div>

          <div className={styles.userDisplayModal_IsActiveContainer}>
            <span
              className={`${
                unsubscribedFromCustomEmails
                  ? styles.userDisPlayModal_InactiveCircleBackground
                  : styles.userDisPlayModal_ActiveCircleBackground
              } material-symbols-outlined`}
            >
              {unsubscribedFromCustomEmails ? "unsubscribe" : "mark_email_read"}
            </span>
            <p className={styles.userDisplayModal_IsActive}>
              {unsubscribedFromCustomEmails
                ? t("components.userDisplayModal.unsubscribedFromCustomEmails")
                : t("components.userDisplayModal.subscribedToCustomEmails")}
            </p>
            {currentUser?.tipoDeUsuario === "5" && (
              <Switch
                checked={!unsubscribedFromCustomEmails}
                onChange={handleToggleCustomEmailSubscription}
                color="primary"
              />
            )}
          </div>

          <hr className={styles.divider} />

          <div className={styles.userDisplayModal_TitleContainer}>
            <div className={styles.userDisplayModal_TitleIconContainer}>
              <span>
                <SvgIcon
                  className={styles.UserDisplayModal_BadgeIcon}
                  component={BadgeIcon}
                />
              </span>
            </div>
            <div className={styles.userDisplayModal_TitleTextContainer}>
              <h5 className={styles.userDisplayModal_TitleLabel}>
                {t("components.userDisplayModal.title")}:
              </h5>
              <h2 className={styles.userDisplayModal_Title}>{titulo}</h2>
            </div>
          </div>

          <div className={styles.userDisplayModal_EmailContainer}>
            <div className={styles.userDisplayModal_EmailIconContainer}>
              <span>
                <SvgIcon
                  className={styles.userDisplayModal_MailIcon}
                  component={MailIcon}
                />
              </span>
            </div>
            <div className={styles.userDisplayModal_EmailTextContainer}>
              <h5 className={styles.userDisplayModal_EmailLabel}>
                {t("components.userDisplayModal.email")}:
              </h5>
              <h2 className={styles.userDisplayModal_Email}>
                {correoElectronico}
              </h2>
            </div>
          </div>

          <div className={styles.userDisplayModal_EmailContainer}>
            <div className={styles.userDisplayModal_EmailIconContainer}>
              <span style={{ width: 24, display: "inline-block" }} />
            </div>
            <div className={styles.userDisplayModal_EmailTextContainer}>
              <h5 className={styles.userDisplayModal_EmailLabel}>
                {t("components.userDisplayModal.phone")}:
              </h5>
              <h2 className={styles.userDisplayModal_Email}>
                {numeroCelular || "—"}
              </h2>
            </div>
          </div>

          <div className={styles.userDisplayModal_RFCContainer}>
            <div className={styles.userDisplayModal_RFCIconContainer}>
              <span>
                <SvgIcon
                  className={styles.userDisplayModal_BusinessCenterIcon}
                  component={BusinessCenterIcon}
                />
              </span>
            </div>
            <div className={styles.userDisplayModal_RFCTextContainer}>
              <h5 className={styles.userDisplayModal_RFCLabel}>
                {t("components.userDisplayModal.rfc")}:
              </h5>
              <h2 className={styles.userDisplayModal_Email}>{RFC}</h2>
            </div>
          </div>

          {currentUser?.tipoDeUsuario !== "3" &&
            currentUser?.tipoDeUsuario !== "4" &&
            currentUser?.tipoDeUsuario !== "12" && (
              <div className={styles.userDisplayModal_ButtonsContainer}>
                <div className={styles.userDisplayModal_EditButtonContainer}>
                  <button
                    className={styles.userDisplayModal_EditButton}
                    onClick={handleEditClick}
                  >
                    <SvgIcon
                      className={styles.userDisplayModal_EditIcon}
                      component={EditIcon}
                    />
                    {t("components.userDisplayModal.editInformation")}
                  </button>
                </div>
              </div>
            )}

          {currentUser?.tipoDeUsuario !== "3" &&
            currentUser?.tipoDeUsuario !== "4" &&
            currentUser?.tipoDeUsuario !== "12" && (
              <div className={styles.modalButtons}>
                <button
                  onClick={() => {
                    toast.dismiss(toastId.current);
                    onClose();
                  }}
                  className={styles.cancelButton}
                >
                  {t("components.userDisplayModal.cancel")}
                </button>
                <button
                  className={styles.createUserButton}
                  onClick={handleUpdateUser}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    t("components.userDisplayModal.save")
                  )}
                </button>
              </div>
            )}
        </div>
      </dialog>

      {/* User Edit Modal */}
      {user && (
        <UserEditModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          user={user}
          onUpdateUser={handleUpdateUserFromEditModal}
        />
      )}
      <ToastContainer />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={isLoadingCompany}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>
    </>
  );
};

export default UserDisplayModal;
